import type { Page } from '../../../types';
import type { IconName } from '../../icons/AppIcon';
import { GearIcon, HomeIcon, MessageCircleIcon, PipelineIcon, RefreshIcon, TaskIcon, UsersIcon } from '../../icons';

export interface SettingsNavItem {
    id: Page;
    label: string;
    icon: IconName;
}

export interface SettingsNavSection {
    title: string;
    items: SettingsNavItem[];
    hidden?: boolean;
}

/** Menu de Configurações (hub mobile + sidebar desktop). */
export function buildSettingsNavSections(
    isMember: boolean,
    canViewBilling: boolean,
): SettingsNavSection[] {
    const sections: SettingsNavSection[] = [
        {
            title: 'CONTA',
            hidden: isMember,
            items: [
                { id: 'team', label: 'Membros', icon: 'users' },
                { id: 'connections', label: 'Canais', icon: 'message' },
                { id: 'tracking', label: 'Integrações', icon: 'link' },
            ],
        },
        {
            title: 'CRM',
            hidden: isMember,
            items: [
                { id: 'products', label: 'Produtos', icon: 'product' },
                { id: 'settings-tags', label: 'Etiquetas', icon: 'tag' },
                { id: 'settings-quick-replies', label: 'Respostas Rápidas', icon: 'chat' },
                { id: 'settings-pix', label: 'Chave PIX', icon: 'pix-line' },
                { id: 'settings-origins', label: 'Origens', icon: 'location' },
                { id: 'settings-loss-reasons', label: 'Motivos de Perda', icon: 'x-circle' },
                { id: 'settings-auto-movement', label: 'Automação', icon: 'zap' },
                { id: 'settings-webhooks', label: 'Webhook', icon: 'webhook' },
            ],
        },
        {
            title: 'MINHA CONTA',
            items: [
                { id: 'profile', label: 'Meus Dados', icon: 'user' },
                ...(!isMember
                    ? [{ id: 'monitoring' as Page, label: 'Utilização', icon: 'activity' as IconName }]
                    : []),
                ...(canViewBilling
                    ? [{ id: 'billing' as Page, label: 'Faturamento', icon: 'credit-card' as IconName }]
                    : []),
            ],
        },
    ];
    return sections.filter((s) => !s.hidden);
}

/** Páginas de conteúdo (hub `settings` fica de fora). Derivado do menu + rota legada. */
export const SETTINGS_CONTENT_PAGES: Page[] = [
    ...buildSettingsNavSections(false, true).flatMap((s) => s.items.map((i) => i.id)),
    'whatsapp', // legado → Canais (`connections`)
];

/** Rotas do hub Configurações — ativas no «Mais» (mobile) e no avatar (desktop). */
export const PROFILE_ROUTE_PAGES: Page[] = [
    'settings',
    ...SETTINGS_CONTENT_PAGES,
    'support',
    'parceiro',
    'parceiro-aplicar',
];

export const NAV_GROUPS = [
    {
        title: 'Visão geral',
        items: [{ id: 'dashboard' as const, label: 'Dashboard', icon: HomeIcon }],
    },
    {
        title: 'Atendimento',
        items: [
            { id: 'chat' as const, label: 'Conversas', icon: MessageCircleIcon },
            { id: 'kanban' as const, label: 'Negociações', icon: PipelineIcon },
        ],
    },
    {
        title: 'Gestão',
        items: [
            { id: 'contacts' as const, label: 'Contatos', icon: UsersIcon },
            { id: 'tasks' as const, label: 'Tarefas', icon: TaskIcon },
            { id: 'follow-up' as const, label: 'Follow-up', icon: RefreshIcon },
        ],
    },
    {
        title: 'Sistema',
        items: [{ id: 'profile' as const, label: 'Configurações', icon: GearIcon }],
    },
] as const;

export type SidebarNavItem = (typeof NAV_GROUPS)[number]['items'][number];

export type MobileNavItem = {
    id: Page;
    label: string;
    mobileLabel: string;
    icon: SidebarNavItem['icon'];
};

/** Mobile: sem engrenagem — «Mais» abre Configurações. Rótulos curtos para caber sem truncar. */
export const MOBILE_NAV_ITEMS: MobileNavItem[] = [
    { id: 'dashboard', label: 'Dashboard', mobileLabel: 'Início', icon: HomeIcon },
    { id: 'chat', label: 'Conversas', mobileLabel: 'Chat', icon: MessageCircleIcon },
    { id: 'kanban', label: 'Negociações', mobileLabel: 'Negócios', icon: PipelineIcon },
    { id: 'contacts', label: 'Contatos', mobileLabel: 'Contatos', icon: UsersIcon },
    { id: 'tasks', label: 'Tarefas', mobileLabel: 'Tarefas', icon: TaskIcon },
];

export const SIDEBAR_ACCOUNT_MENU_ITEM_CLASS =
    'cursor-pointer px-s4 py-2.5 text-text-secondary focus:bg-surface-subtle data-highlighted:bg-surface-subtle focus:text-text data-highlighted:text-text';
